import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Sliders,
  Save,
  CheckCircle2,
  MessageSquare,
  Zap,
  Settings2,
  CreditCard,
  Eye,
  EyeOff,
  Loader2,
  ShieldCheck,
  Info,
  LayoutDashboard,
  Network,
  Users,
  Wallet,
  FileBarChart2,
  Database,
  Receipt,
  CalendarClock,
  Percent,
  Landmark,
  Building2,
  PenLine,
  QrCode,
  Plus,
  Trash2,
  AppWindow,
  Image as AppImage,
  Upload,
  X,
  ShieldOff,
} from "lucide-react";
import { MENU, BUILTIN_ROLES, allModuleKeys } from "../config/modules";
import { IsolirSettingsTab } from "../components/settings/IsolirSettingsTab";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const DEFAULT_FORM = {
  // Identitas Aplikasi
  app_name: "NUSA NET FIBER",
  app_description: "Sistem Informasi Billing & Manajemen Jaringan Internet",
  app_logo: "",
  app_favicon: "",
  // Umum
  billing_cycle_date: 10,
  grace_period_days: 5,
  auto_isolir_enabled: true,
  isolir_otomatis_aktif: false,
  isolir_profile: "ISOLIR",
  isolir_page_html: "",
  mikrotik_auto_sync: true,
  timezone: "Asia/Jakarta",
  currency_symbol: "Rp",
  language: "id",
  // WhatsApp
  wa_gateway_enabled: true,
  wa_provider: "wa_me",  // wa_me, fonnte, twilio, wablas
  wa_gateway_endpoint: "https://api.fonnte.com/send",
  wa_gateway_token: "",
  wa_sender_number: "",
  wa_fonnte_token: "",
  wa_wablas_token: "",
  wa_wablas_secret: "",
  wa_wablas_endpoint: "https://wablas.com/api/send-message",
  wa_watzap_api_key: "",
  wa_watzap_number_key: "",
  wa_twilio_sid: "",
  wa_twilio_token: "",
  wa_twilio_from: "",
  wa_custom_vars: [],
  wa_template_tagihan: "Halo Bapak/Ibu {nama},\n\nBerikut tagihan internet {no_invoice} periode {periode} senilai Rp {total}.\nJatuh tempo: {jatuh_tempo}.\n\nCek & bayar tagihan lewat Portal Pelanggan:\n{link_portal}\nNo. Layanan: {no_layanan} (sandi awal 123456)\n\nTerima kasih.",
  wa_template_lunas: "Terima kasih Bapak/Ibu {nama}, pembayaran #{no_invoice} sebesar Rp {total} telah kami terima. Layanan Anda AKTIF.\n\nRiwayat tagihan: {link_portal} (No. Layanan {no_layanan})",
  wa_template_isolir: "Mohon maaf Bapak/Ibu {nama}, layanan internet Anda ({no_layanan}) telah diisolir karena tagihan {no_invoice} belum dilunasi. Silakan segera melakukan pembayaran.\n\nBayar online: {link_portal}",
  // Payment Gateway
  payment_enabled: false,
  payment_provider: "tripay",  // tripay, xendit, midtrans, stripe
  payment_mode: "sandbox",  // sandbox, production
  payment_server_key: "",
  payment_client_key: "",
  payment_merchant_code: "",
  // Provider-specific credentials
  tripay_api_key: "",
  tripay_private_key: "",
  tripay_merchant_code: "",
  xendit_secret_key: "",
  xendit_webhook_token: "",
  midtrans_server_key: "",
  midtrans_client_key: "",
  payment_qris_enabled: true,
  payment_va_enabled: true,
  payment_ewallet_enabled: true,
  payment_success_url: "",
  payment_notification_url: "",
  // Visibility
  visible_dashboard: true,
  visible_jaringan: true,
  visible_pelanggan: true,
  visible_keuangan: true,
  visible_laporan: true,
  visible_master: true,
  role_admin_full: true,
  role_finance_scope: ["dashboard", "pelanggan", "keuangan", "laporan"],
  role_teknisi_scope: ["dashboard", "jaringan", "pelanggan"],
  // Visibility & akses granular (baru)
  module_visibility: {},
  role_access: {},
  custom_roles: [],
  // Invoice
  invoice_due_date: 10,
  invoice_tax_enabled: false,
  invoice_tax_percent: 11,
  invoice_tax_inclusive: false,
  invoice_payment_methods: [],
  invoice_signer_name: "",
  invoice_signer_title: "Finance Dept.",
  invoice_barcode_enabled: true,
  invoice_barcode_type: "qrcode",
  invoice_notes: [],
  invoice_show_isp: true,
  invoice_isp_position: "left",
  invoice_show_reseller: false,
  invoice_reseller_position: "right",
  invoice_signature_type: "barcode",
  invoice_signature_image: "",
  invoice_stamp_image: "",
};

const TABS = [
  { id: "umum", label: "Umum", icon: Settings2, color: "cyan" },
  { id: "invoice", label: "Invoice", icon: Receipt, color: "rose" },
  { id: "wa", label: "WhatsApp Gateway", icon: MessageSquare, color: "emerald" },
  { id: "payment", label: "Payment Gateway", icon: CreditCard, color: "amber" },
  { id: "isolir", label: "Isolir", icon: ShieldOff, color: "rose" },
  { id: "visibility", label: "Visibility", icon: Eye, color: "purple" },
];

export const PengaturanAplikasiPage = () => {
  const [form, setForm] = useState(DEFAULT_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState("");
  const [activeTab, setActiveTab] = useState("umum");
  const [showToken, setShowToken] = useState(false);
  const [showServerKey, setShowServerKey] = useState(false);
  const [payTest, setPayTest] = useState({ loading: false, ok: null, message: "" });
  const [dueDates, setDueDates] = useState([]);

  const handleTestPayment = async () => {
    setPayTest({ loading: true, ok: null, message: "" });
    try {
      const res = await axios.post(`${BACKEND_URL}/api/payments/test-connection`, form, { withCredentials: true });
      setPayTest({ loading: false, ok: true, message: res.data.message });
    } catch (err) {
      setPayTest({ loading: false, ok: false, message: err.response?.data?.detail || "Gagal menghubungi gateway" });
    }
  };
  const [imgError, setImgError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const [res, resPel] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/pengaturan/aplikasi`, { withCredentials: true }),
          axios.get(`${BACKEND_URL}/api/pelanggan`, { withCredentials: true }).catch(() => ({ data: [] })),
        ]);
        if (res.data) setForm({ ...DEFAULT_FORM, ...res.data });
        const days = Array.from(
          new Set((resPel.data || []).map((c) => c.jatuh_tempo_hari).filter((d) => d != null))
        ).sort((a, b) => a - b);
        setDueDates(days);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    })();
  }, []);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addBank = () => setField("invoice_payment_methods", [...(form.invoice_payment_methods || []), { bank: "", no_rekening: "", atas_nama: "" }]);
  const updateBank = (i, k, v) => setField("invoice_payment_methods", (form.invoice_payment_methods || []).map((b, idx) => (idx === i ? { ...b, [k]: v } : b)));
  const removeBank = (i) => setField("invoice_payment_methods", (form.invoice_payment_methods || []).filter((_, idx) => idx !== i));

  const addNote = () => setField("invoice_notes", [...(form.invoice_notes || []), ""]);
  const updateNote = (i, v) => setField("invoice_notes", (form.invoice_notes || []).map((n, idx) => (idx === i ? v : n)));
  const removeNote = (i) => setField("invoice_notes", (form.invoice_notes || []).filter((_, idx) => idx !== i));

  const addCustomVar = () => setField("wa_custom_vars", [...(form.wa_custom_vars || []), { key: "", value: "" }]);
  const updateCustomVar = (i, k, v) => setField("wa_custom_vars", (form.wa_custom_vars || []).map((c, idx) => (idx === i ? { ...c, [k]: v } : c)));
  const removeCustomVar = (i) => setField("wa_custom_vars", (form.wa_custom_vars || []).filter((_, idx) => idx !== i));

  const handleImg = (key, file) => {
    setImgError("");
    if (!file) return;
    if (!file.type.startsWith("image/")) { setImgError("File harus berupa gambar (PNG/JPG/SVG)"); return; }
    if (file.size > 500 * 1024) { setImgError("Ukuran gambar maksimal 500KB"); return; }
    const reader = new FileReader();
    reader.onload = () => setField(key, reader.result);
    reader.readAsDataURL(file);
  };

  const handleSave = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/pengaturan/aplikasi`, form, { withCredentials: true });
      setNotification("Pengaturan aplikasi berhasil disimpan!");
      setTimeout(() => setNotification(""), 4000);
    } catch (err) {
      alert("Gagal menyimpan pengaturan aplikasi");
    } finally {
      setSaving(false);
    }
  };

  const toggleRoleScope = (role, module) => {
    const key = `role_${role}_scope`;
    const current = form[key] || [];
    const updated = current.includes(module) ? current.filter((x) => x !== module) : [...current, module];
    setField(key, updated);
  };

  // ==== Visibility & Akses granular ====
  const [newRole, setNewRole] = useState({ label: "", desc: "" });
  const moduleVisible = (key) => form.module_visibility?.[key] !== false;
  const toggleModuleVisible = (key) => setField("module_visibility", { ...(form.module_visibility || {}), [key]: !moduleVisible(key) });
  const roleHas = (roleId, key) => {
    const scope = form.role_access?.[roleId];
    return scope ? scope.includes(key) : true; // belum dikonfigurasi => semua aktif
  };
  const toggleRoleModule = (roleId, key) => {
    const current = form.role_access?.[roleId] ?? allModuleKeys();
    const updated = current.includes(key) ? current.filter((x) => x !== key) : [...current, key];
    setField("role_access", { ...(form.role_access || {}), [roleId]: updated });
  };
  const slugRole = (s) => (s || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
  const addCustomRole = () => {
    const id = slugRole(newRole.label);
    if (!id) return;
    if (BUILTIN_ROLES.some((r) => r.id === id) || (form.custom_roles || []).some((r) => r.id === id)) { alert("Hak akses dengan nama itu sudah ada"); return; }
    setField("custom_roles", [...(form.custom_roles || []), { id, label: newRole.label.trim(), desc: newRole.desc.trim() }]);
    setField("role_access", { ...(form.role_access || {}), [id]: allModuleKeys() });
    setNewRole({ label: "", desc: "" });
  };
  const removeCustomRole = (id) => {
    setField("custom_roles", (form.custom_roles || []).filter((r) => r.id !== id));
    const ra = { ...(form.role_access || {}) }; delete ra[id]; setField("role_access", ra);
  };
  const allRoles = [...BUILTIN_ROLES, ...(form.custom_roles || []).map((r) => ({ ...r, custom: true }))];
  const chipCls = (active, small) => `${small ? "px-2 py-0.5 text-[10px]" : "px-2.5 py-1 text-[10px]"} rounded-full font-bold transition-colors ${active ? "bg-purple-500/30 text-purple-200 border border-purple-500/50" : "bg-slate-800 text-slate-400 border border-slate-700 hover:border-slate-600"}`;

  const dueOptions = Array.from(new Set([...dueDates, 1, 5, 10, 15, 20, 25, 28])).sort((a, b) => a - b);

  if (loading) {
    return (
      <div className="p-6 text-center text-slate-400 text-sm">
        <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Memuat pengaturan...
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-5xl mx-auto" data-testid="pengaturan-aplikasi-page">
      <div className="p-5 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-xl">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-teal-500/20 text-teal-400 flex items-center justify-center">
            <Sliders className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">Pengaturan Aplikasi</h1>
            <p className="text-xs text-slate-400">Konfigurasi billing cycle, WA gateway, payment gateway, dan visibility modul</p>
          </div>
        </div>
      </div>

      {notification && (
        <div className="p-3 bg-teal-500/10 border border-teal-500/30 text-teal-300 rounded-xl text-xs flex items-center gap-2" data-testid="save-notification">
          <CheckCircle2 className="w-4 h-4 text-teal-400" />
          <span>{notification}</span>
        </div>
      )}

      {/* Tabs */}
      <div className="rounded-2xl bg-[#0e1628] border border-slate-800 shadow-xl overflow-hidden">
        <div className="flex items-center gap-1 border-b border-slate-800 bg-slate-950/60 px-2 overflow-x-auto" data-testid="aplikasi-tabs">
          {TABS.map((t) => {
            const Icon = t.icon;
            const isActive = activeTab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                data-testid={`tab-${t.id}`}
                className={`px-4 py-3 text-xs font-bold flex items-center gap-2 border-b-2 transition-colors whitespace-nowrap ${
                  isActive
                    ? `border-${t.color}-400 text-${t.color}-300`
                    : "border-transparent text-slate-400 hover:text-slate-200"
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                <span>{t.label}</span>
              </button>
            );
          })}
        </div>

        <form onSubmit={handleSave} className="p-5 space-y-5">
          {/* TAB UMUM */}
          {activeTab === "umum" && (
            <div className="space-y-5" data-testid="tab-umum-content">
              <div className="p-4 bg-slate-900 border border-slate-800 rounded-xl space-y-4" data-testid="app-identity-card">
                <h3 className="text-sm font-bold text-white flex items-center gap-2">
                  <AppWindow className="w-4 h-4 text-cyan-400" />Identitas Aplikasi
                </h3>
                {imgError && (
                  <div className="p-2.5 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-300 text-[11px] flex items-center gap-2"><Info className="w-3.5 h-3.5" />{imgError}</div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-slate-300 mb-1">Nama Aplikasi</label>
                    <input type="text" value={form.app_name} onChange={(e) => setField("app_name", e.target.value)} placeholder="NUSA NET FIBER" className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white" data-testid="app-name-input" />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-300 mb-1">Deskripsi</label>
                    <input type="text" value={form.app_description} onChange={(e) => setField("app_description", e.target.value)} placeholder="Sistem Billing & Manajemen Jaringan" className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white" data-testid="app-desc-input" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-950 border border-slate-800">
                    <div className="w-16 h-16 rounded-xl bg-slate-900 border border-slate-700 flex items-center justify-center overflow-hidden flex-shrink-0">
                      {form.app_logo ? <img src={form.app_logo} alt="Logo Aplikasi" className="w-full h-full object-contain" data-testid="app-logo-preview" /> : <AppImage className="w-6 h-6 text-slate-600" />}
                    </div>
                    <div className="flex-1">
                      <p className="text-xs font-bold text-white mb-0.5">Logo Aplikasi</p>
                      <p className="text-[10px] text-slate-500 mb-2">PNG/JPG/SVG, maks 500KB</p>
                      <div className="flex gap-2">
                        <label className="px-2.5 py-1.5 rounded-lg bg-cyan-500/20 text-cyan-300 hover:bg-cyan-500/30 text-[11px] font-bold flex items-center gap-1 cursor-pointer">
                          <Upload className="w-3.5 h-3.5" /> Upload
                          <input type="file" accept="image/*" className="hidden" data-testid="app-logo-input" onChange={(e) => handleImg("app_logo", e.target.files?.[0])} />
                        </label>
                        {form.app_logo && <button type="button" onClick={() => setField("app_logo", "")} data-testid="app-logo-remove" className="px-2.5 py-1.5 rounded-lg bg-slate-800 text-slate-400 hover:text-rose-300 text-[11px] font-bold flex items-center gap-1"><X className="w-3.5 h-3.5" /></button>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-950 border border-slate-800">
                    <div className="w-16 h-16 rounded-xl bg-slate-900 border border-slate-700 flex items-center justify-center overflow-hidden flex-shrink-0">
                      {form.app_favicon ? <img src={form.app_favicon} alt="Favicon" className="w-full h-full object-contain" data-testid="app-favicon-preview" /> : <AppImage className="w-6 h-6 text-slate-600" />}
                    </div>
                    <div className="flex-1">
                      <p className="text-xs font-bold text-white mb-0.5">Logo Favicon</p>
                      <p className="text-[10px] text-slate-500 mb-2">Ikon tab browser, gambar persegi</p>
                      <div className="flex gap-2">
                        <label className="px-2.5 py-1.5 rounded-lg bg-cyan-500/20 text-cyan-300 hover:bg-cyan-500/30 text-[11px] font-bold flex items-center gap-1 cursor-pointer">
                          <Upload className="w-3.5 h-3.5" /> Upload
                          <input type="file" accept="image/*" className="hidden" data-testid="app-favicon-input" onChange={(e) => handleImg("app_favicon", e.target.files?.[0])} />
                        </label>
                        {form.app_favicon && <button type="button" onClick={() => setField("app_favicon", "")} data-testid="app-favicon-remove" className="px-2.5 py-1.5 rounded-lg bg-slate-800 text-slate-400 hover:text-rose-300 text-[11px] font-bold flex items-center gap-1"><X className="w-3.5 h-3.5" /></button>}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-slate-900 border border-slate-800 rounded-xl space-y-4">
                <h3 className="text-sm font-bold text-white flex items-center gap-2">
                  <Zap className="w-4 h-4 text-cyan-400" />Sinkronisasi MikroTik
                </h3>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={form.mikrotik_auto_sync} onChange={(e) => setField("mikrotik_auto_sync", e.target.checked)} className="w-4 h-4 accent-cyan-500" data-testid="mikrotik-sync-toggle" />
                  <span className="text-xs text-slate-200">Aktifkan Auto-Sync Secret PPPoE ke Router Mikrotik</span>
                </label>
              </div>

              <div className="p-4 bg-slate-900 border border-slate-800 rounded-xl space-y-4">
                <h3 className="text-sm font-bold text-white flex items-center gap-2"><Settings2 className="w-4 h-4 text-indigo-400" />Preferensi Regional</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs text-slate-300 mb-1">Zona Waktu</label>
                    <select value={form.timezone} onChange={(e) => setField("timezone", e.target.value)} className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white">
                      <option value="Asia/Jakarta">WIB (UTC+7)</option>
                      <option value="Asia/Makassar">WITA (UTC+8)</option>
                      <option value="Asia/Jayapura">WIT (UTC+9)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-slate-300 mb-1">Simbol Mata Uang</label>
                    <input type="text" value={form.currency_symbol} onChange={(e) => setField("currency_symbol", e.target.value)} className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white font-mono" />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-300 mb-1">Bahasa Aplikasi</label>
                    <select value={form.language} onChange={(e) => setField("language", e.target.value)} className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white">
                      <option value="id">Bahasa Indonesia</option>
                      <option value="en">English</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "isolir" && <IsolirSettingsTab form={form} setField={setField} />}

          {/* TAB INVOICE */}
          {activeTab === "invoice" && (
            <div className="space-y-5" data-testid="tab-invoice-content">
              {/* Jatuh Tempo */}
              <div className="p-4 bg-slate-900 border border-slate-800 rounded-xl space-y-4">
                <h3 className="text-sm font-bold text-white flex items-center gap-2"><CalendarClock className="w-4 h-4 text-rose-400" />Tanggal Jatuh Tempo Invoice</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-slate-300 mb-1">Pilih Tanggal Jatuh Tempo</label>
                    <select value={form.invoice_due_date} onChange={(e) => setField("invoice_due_date", parseInt(e.target.value))} className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white" data-testid="invoice-due-date-select">
                      {dueOptions.map((d) => (
                        <option key={d} value={d}>Tanggal {d}{dueDates.includes(d) ? " • ada di data pelanggan" : ""}</option>
                      ))}
                    </select>
                    <p className="text-[10px] text-slate-500 mt-1 flex items-center gap-1"><Info className="w-3 h-3" />Opsi mengikuti tanggal tagihan pada Data Pelanggan{dueDates.length ? ` (${dueDates.join(", ")})` : " (belum ada data, memakai preset standar)"}</p>
                  </div>
                </div>
              </div>

              {/* Identitas pada Invoice */}
              <div className="p-4 bg-slate-900 border border-slate-800 rounded-xl space-y-4" data-testid="invoice-identity-card">
                <h3 className="text-sm font-bold text-white flex items-center gap-2"><Building2 className="w-4 h-4 text-rose-400" />Identitas pada Invoice</h3>
                <p className="text-[10px] text-slate-500 flex items-center gap-1"><Info className="w-3 h-3" />Atur logo &amp; identitas yang tampil di kop invoice. Data diambil dari Pengaturan Perusahaan (ISP Induk &amp; Reseller/Mitra).</p>

                <div className="p-3 rounded-xl bg-slate-950 border border-slate-800 space-y-3">
                  <label className="flex items-center justify-between gap-3 cursor-pointer">
                    <span className="text-xs text-slate-200"><b className="text-white">1.</b> Tampilkan Logo ISP dan identitas ISP Induk ke dalam invoice</span>
                    <div className="relative inline-flex items-center shrink-0">
                      <input type="checkbox" checked={form.invoice_show_isp} onChange={(e) => setField("invoice_show_isp", e.target.checked)} className="sr-only peer" data-testid="invoice-show-isp-toggle" />
                      <div className="w-10 h-5 bg-slate-700 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-rose-600"></div>
                    </div>
                  </label>
                  {form.invoice_show_isp && (
                    <div>
                      <label className="block text-[10px] text-slate-400 mb-1">Posisi di invoice</label>
                      <select value={form.invoice_isp_position} onChange={(e) => setField("invoice_isp_position", e.target.value)} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-white" data-testid="invoice-isp-position-select">
                        <option value="left">Kiri Atas</option>
                        <option value="right">Kanan Atas</option>
                      </select>
                    </div>
                  )}
                </div>

                <div className="p-3 rounded-xl bg-slate-950 border border-slate-800 space-y-3">
                  <label className="flex items-center justify-between gap-3 cursor-pointer">
                    <span className="text-xs text-slate-200"><b className="text-white">2.</b> Tampilkan Logo dan identitas mitra/reseller ke invoice</span>
                    <div className="relative inline-flex items-center shrink-0">
                      <input type="checkbox" checked={form.invoice_show_reseller} onChange={(e) => setField("invoice_show_reseller", e.target.checked)} className="sr-only peer" data-testid="invoice-show-reseller-toggle" />
                      <div className="w-10 h-5 bg-slate-700 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-rose-600"></div>
                    </div>
                  </label>
                  {form.invoice_show_reseller && (
                    <div>
                      <label className="block text-[10px] text-slate-400 mb-1">Posisi di invoice</label>
                      <select value={form.invoice_reseller_position} onChange={(e) => setField("invoice_reseller_position", e.target.value)} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-white" data-testid="invoice-reseller-position-select">
                        <option value="left">Kiri Atas</option>
                        <option value="right">Kanan Atas</option>
                      </select>
                    </div>
                  )}
                </div>
              </div>

              {/* Pajak */}
              <div className="p-4 bg-slate-900 border border-slate-800 rounded-xl space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-bold text-white flex items-center gap-2"><Percent className="w-4 h-4 text-rose-400" />Pajak (PPN)</h3>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" checked={form.invoice_tax_enabled} onChange={(e) => setField("invoice_tax_enabled", e.target.checked)} className="sr-only peer" data-testid="invoice-tax-toggle" />
                    <div className="w-10 h-5 bg-slate-700 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-rose-600"></div>
                  </label>
                </div>
                {form.invoice_tax_enabled && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs text-slate-300 mb-1">Persentase Pajak (%)</label>
                        <input type="number" min={0} step="0.1" value={form.invoice_tax_percent} onChange={(e) => setField("invoice_tax_percent", parseFloat(e.target.value) || 0)} className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white font-mono" data-testid="invoice-tax-percent-input" />
                      </div>
                      <div>
                        <label className="block text-xs text-slate-300 mb-1">Perlakuan Harga Paket</label>
                        <select value={form.invoice_tax_inclusive ? "inclusive" : "exclusive"} onChange={(e) => setField("invoice_tax_inclusive", e.target.value === "inclusive")} className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white" data-testid="invoice-tax-mode-select">
                          <option value="exclusive">Belum termasuk pajak (pajak ditambahkan)</option>
                          <option value="inclusive">Sudah termasuk pajak (include)</option>
                        </select>
                      </div>
                    </div>
                    <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/30 text-[11px] text-rose-100 space-y-0.5" data-testid="invoice-tax-example">
                      {(() => {
                        const base = 120000; const p = Number(form.invoice_tax_percent) || 0;
                        if (form.invoice_tax_inclusive) {
                          const dpp = base / (1 + p / 100); const tax = base - dpp;
                          return (<><p>Contoh paket <b>Rp 120.000</b> (sudah termasuk pajak {p}%):</p><p>DPP: Rp {Math.round(dpp).toLocaleString("id-ID")} · Pajak: Rp {Math.round(tax).toLocaleString("id-ID")} · Total tetap: <b>Rp {base.toLocaleString("id-ID")}</b></p></>);
                        }
                        const tax = base * p / 100; const total = base + tax;
                        return (<><p>Contoh paket <b>Rp 120.000</b> (belum termasuk pajak {p}%):</p><p>Pajak: Rp {Math.round(tax).toLocaleString("id-ID")} · Total dibayar: <b>Rp {Math.round(total).toLocaleString("id-ID")}</b></p></>);
                      })()}
                    </div>
                  </div>
                )}
              </div>

              {/* Metode Pembayaran / Akun Bank */}
              <div className="p-4 bg-slate-900 border border-slate-800 rounded-xl space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-bold text-white flex items-center gap-2"><Landmark className="w-4 h-4 text-rose-400" />Metode Pembayaran / Akun Bank</h3>
                  <button type="button" onClick={addBank} data-testid="add-bank-btn" className="px-2.5 py-1.5 rounded-lg bg-rose-500/20 text-rose-300 hover:bg-rose-500/30 text-[11px] font-bold flex items-center gap-1"><Plus className="w-3.5 h-3.5" />Tambah</button>
                </div>
                <p className="text-[10px] text-slate-500">Rekening yang tampil di invoice sebagai tujuan transfer manual.</p>
                {(form.invoice_payment_methods || []).length === 0 && (
                  <p className="text-[11px] text-slate-500 italic">Belum ada akun bank. Klik "Tambah" untuk menambahkan.</p>
                )}
                {(form.invoice_payment_methods || []).map((b, i) => (
                  <div key={i} className="grid grid-cols-1 sm:grid-cols-[1fr_1.2fr_1.2fr_auto] gap-2 items-end" data-testid={`bank-row-${i}`}>
                    <div>
                      <label className="block text-[10px] text-slate-400 mb-1">Bank / E-Wallet</label>
                      <input type="text" value={b.bank || ""} onChange={(e) => updateBank(i, "bank", e.target.value)} placeholder="BCA" className="w-full px-2.5 py-2 bg-slate-950 border border-slate-700 rounded-lg text-xs text-white" data-testid={`bank-name-${i}`} />
                    </div>
                    <div>
                      <label className="block text-[10px] text-slate-400 mb-1">No. Rekening</label>
                      <input type="text" value={b.no_rekening || ""} onChange={(e) => updateBank(i, "no_rekening", e.target.value)} placeholder="1234567890" className="w-full px-2.5 py-2 bg-slate-950 border border-slate-700 rounded-lg text-xs text-white font-mono" data-testid={`bank-no-${i}`} />
                    </div>
                    <div>
                      <label className="block text-[10px] text-slate-400 mb-1">Atas Nama</label>
                      <input type="text" value={b.atas_nama || ""} onChange={(e) => updateBank(i, "atas_nama", e.target.value)} placeholder="PT NUSA MEDIA" className="w-full px-2.5 py-2 bg-slate-950 border border-slate-700 rounded-lg text-xs text-white" data-testid={`bank-holder-${i}`} />
                    </div>
                    <button type="button" onClick={() => removeBank(i)} data-testid={`remove-bank-${i}`} className="p-2 rounded-lg bg-slate-800 hover:bg-rose-900/60 text-slate-400 hover:text-rose-300"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
              </div>

              {/* Penandatanganan */}
              <div className="p-4 bg-slate-900 border border-slate-800 rounded-xl space-y-4">
                <h3 className="text-sm font-bold text-white flex items-center gap-2"><PenLine className="w-4 h-4 text-rose-400" />Penandatanganan Invoice</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-slate-300 mb-1">Nama Penandatangan</label>
                    <input type="text" value={form.invoice_signer_name} onChange={(e) => setField("invoice_signer_name", e.target.value)} placeholder="Nama Manajer / Finance" className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white" data-testid="invoice-signer-name-input" />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-300 mb-1">Jabatan</label>
                    <input type="text" value={form.invoice_signer_title} onChange={(e) => setField("invoice_signer_title", e.target.value)} placeholder="Finance Dept." className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white" data-testid="invoice-signer-title-input" />
                  </div>
                </div>
                <label className="flex items-center justify-between gap-3 p-3 rounded-xl border border-slate-700 bg-slate-950 cursor-pointer">
                  <div className="flex items-center gap-2">
                    <QrCode className="w-4 h-4 text-rose-400" />
                    <div>
                      <p className="text-xs font-bold text-white">Jenis Penandatanganan</p>
                      <p className="text-[10px] text-slate-500">Pilih validasi invoice: barcode otomatis atau tanda tangan &amp; stempel.</p>
                    </div>
                  </div>
                  <select value={form.invoice_signature_type} onChange={(e) => setField("invoice_signature_type", e.target.value)} className="px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-white" data-testid="invoice-signature-type-select">
                    <option value="barcode">Barcode Otomatis</option>
                    <option value="manual">Tanda Tangan &amp; Stempel Biasa</option>
                  </select>
                </label>
                {form.invoice_signature_type !== "manual" && (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3" data-testid="signature-barcode-options">
                    <div className="flex-1">
                      <label className="block text-xs text-slate-300 mb-1">Tipe Kode</label>
                      <select value={form.invoice_barcode_type} onChange={(e) => setField("invoice_barcode_type", e.target.value)} className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white" data-testid="invoice-barcode-type-select">
                        <option value="qrcode">QR Code</option>
                        <option value="barcode">Barcode (Code 128)</option>
                      </select>
                    </div>
                    <div className="flex items-center gap-2 p-2 rounded-lg bg-white" data-testid="barcode-preview">
                      {form.invoice_barcode_type === "qrcode" ? (
                        <div className="w-14 h-14 grid grid-cols-4 grid-rows-4 gap-0.5">
                          {Array.from({ length: 16 }).map((_, i) => (<div key={i} className={[0, 1, 2, 3, 4, 7, 8, 11, 12, 13, 14, 15, 5, 10].includes(i) ? "bg-black" : "bg-white"} />))}
                        </div>
                      ) : (
                        <div className="flex items-end gap-[2px] h-12">
                          {Array.from({ length: 24 }).map((_, i) => (<div key={i} className="bg-black" style={{ width: (i % 3 ? 1 : 2) + "px", height: "100%" }} />))}
                        </div>
                      )}
                      <span className="text-[9px] font-mono text-slate-700">INV-PREVIEW</span>
                    </div>
                  </div>
                )}
                {form.invoice_signature_type === "manual" && (
                  <div className="space-y-3" data-testid="signature-manual-options">
                    <p className="text-[10px] text-slate-500 flex items-center gap-1"><Info className="w-3 h-3" />Unggah tanda tangan digital (dan stempel bila ada). PNG transparan disarankan, maks 500KB. Akan tampil di area tanda tangan invoice.</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-950 border border-slate-800">
                        <div className="w-20 h-16 rounded-lg bg-white border border-slate-700 flex items-center justify-center overflow-hidden flex-shrink-0">
                          {form.invoice_signature_image ? <img src={form.invoice_signature_image} alt="Tanda Tangan" className="w-full h-full object-contain" data-testid="invoice-signature-preview" /> : <PenLine className="w-6 h-6 text-slate-400" />}
                        </div>
                        <div className="flex-1">
                          <p className="text-xs font-bold text-white mb-0.5">Tanda Tangan Digital</p>
                          <p className="text-[10px] text-slate-500 mb-2">PNG transparan, maks 500KB</p>
                          <div className="flex gap-2">
                            <label className="px-2.5 py-1.5 rounded-lg bg-rose-500/20 text-rose-300 hover:bg-rose-500/30 text-[11px] font-bold flex items-center gap-1 cursor-pointer">
                              <Upload className="w-3.5 h-3.5" /> Upload
                              <input type="file" accept="image/*" className="hidden" data-testid="invoice-signature-input" onChange={(e) => handleImg("invoice_signature_image", e.target.files?.[0])} />
                            </label>
                            {form.invoice_signature_image && <button type="button" onClick={() => setField("invoice_signature_image", "")} data-testid="invoice-signature-remove" className="px-2.5 py-1.5 rounded-lg bg-slate-800 text-slate-400 hover:text-rose-300 text-[11px] font-bold flex items-center gap-1"><X className="w-3.5 h-3.5" /></button>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-950 border border-slate-800">
                        <div className="w-20 h-16 rounded-lg bg-white border border-slate-700 flex items-center justify-center overflow-hidden flex-shrink-0">
                          {form.invoice_stamp_image ? <img src={form.invoice_stamp_image} alt="Stempel" className="w-full h-full object-contain" data-testid="invoice-stamp-preview" /> : <ShieldCheck className="w-6 h-6 text-slate-400" />}
                        </div>
                        <div className="flex-1">
                          <p className="text-xs font-bold text-white mb-0.5">Stempel (Opsional)</p>
                          <p className="text-[10px] text-slate-500 mb-2">PNG transparan, maks 500KB</p>
                          <div className="flex gap-2">
                            <label className="px-2.5 py-1.5 rounded-lg bg-rose-500/20 text-rose-300 hover:bg-rose-500/30 text-[11px] font-bold flex items-center gap-1 cursor-pointer">
                              <Upload className="w-3.5 h-3.5" /> Upload
                              <input type="file" accept="image/*" className="hidden" data-testid="invoice-stamp-input" onChange={(e) => handleImg("invoice_stamp_image", e.target.files?.[0])} />
                            </label>
                            {form.invoice_stamp_image && <button type="button" onClick={() => setField("invoice_stamp_image", "")} data-testid="invoice-stamp-remove" className="px-2.5 py-1.5 rounded-lg bg-slate-800 text-slate-400 hover:text-rose-300 text-[11px] font-bold flex items-center gap-1"><X className="w-3.5 h-3.5" /></button>}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Catatan Invoice (Note) */}
              <div className="p-4 bg-slate-900 border border-slate-800 rounded-xl space-y-3" data-testid="invoice-notes-card">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-bold text-white flex items-center gap-2"><Receipt className="w-4 h-4 text-rose-400" />Catatan Invoice (Note)</h3>
                  <button type="button" onClick={addNote} data-testid="add-note-btn" className="px-2.5 py-1.5 rounded-lg bg-rose-500/20 text-rose-300 hover:bg-rose-500/30 text-[11px] font-bold flex items-center gap-1"><Plus className="w-3.5 h-3.5" />Tambah Baris</button>
                </div>
                <p className="text-[10px] text-slate-500">Catatan/keterangan tambahan yang tampil di invoice cetak (mis. syarat &amp; ketentuan, info promo). Bisa lebih dari satu baris.</p>
                {(form.invoice_notes || []).length === 0 && (
                  <p className="text-[11px] text-slate-500 italic">Belum ada catatan. Klik "Tambah Baris" untuk menambahkan.</p>
                )}
                {(form.invoice_notes || []).map((n, i) => (
                  <div key={i} className="flex items-center gap-2" data-testid={`note-row-${i}`}>
                    <span className="text-[10px] text-slate-500 font-mono w-5 text-right shrink-0">{i + 1}.</span>
                    <input type="text" value={n || ""} onChange={(e) => updateNote(i, e.target.value)} placeholder="Tulis catatan yang tampil di invoice..." className="flex-1 px-2.5 py-2 bg-slate-950 border border-slate-700 rounded-lg text-xs text-white" data-testid={`note-input-${i}`} />
                    <button type="button" onClick={() => removeNote(i)} data-testid={`remove-note-${i}`} className="p-2 rounded-lg bg-slate-800 hover:bg-rose-900/60 text-slate-400 hover:text-rose-300 shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* TAB WHATSAPP */}
          {activeTab === "wa" && (
            <div className="space-y-5" data-testid="tab-wa-content">
              <div className="p-4 bg-slate-900 border border-slate-800 rounded-xl space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-bold text-white flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-emerald-400" />Konfigurasi WhatsApp Gateway
                  </h3>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" checked={form.wa_gateway_enabled} onChange={(e) => setField("wa_gateway_enabled", e.target.checked)} className="sr-only peer" data-testid="wa-enabled-toggle" />
                    <div className="w-10 h-5 bg-slate-700 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-600"></div>
                  </label>
                </div>

                <div>
                  <label className="block text-xs text-slate-300 mb-1">Provider</label>
                  <select value={form.wa_provider} onChange={(e) => setField("wa_provider", e.target.value)} className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white" data-testid="wa-provider-select">
                    <option value="wa_me">wa.me Link (Klik-Kirim Manual — GRATIS)</option>
                    <option value="fonnte">Fonnte (Indonesia — populer)</option>
                    <option value="wablas">Wablas (Indonesia)</option>
                    <option value="watzap">Watzap (Indonesia)</option>
                    <option value="twilio">Twilio WhatsApp Business (Global)</option>
                  </select>
                  <p className="text-[10px] text-slate-500 mt-1 flex items-center gap-1"><Info className="w-3 h-3" />Provider selain wa.me memerlukan API key & pendaftaran akun</p>
                </div>

                {form.wa_provider === "fonnte" && (
                  <div>
                    <label className="block text-xs text-slate-300 mb-1">Fonnte Token</label>
                    <input type={showToken ? "text" : "password"} value={form.wa_fonnte_token || ""} onChange={(e) => setField("wa_fonnte_token", e.target.value)} placeholder="Token perangkat Fonnte" className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white font-mono" data-testid="wa-fonnte-token" />
                    <p className="text-[10px] text-slate-500 mt-1">Ambil dari dashboard Fonnte → Device → Token.</p>
                  </div>
                )}
                {form.wa_provider === "wablas" && (
                  <div className="space-y-3" data-testid="wablas-fields">
                    <div>
                      <label className="block text-xs text-slate-300 mb-1">Wablas Endpoint</label>
                      <input type="url" value={form.wa_wablas_endpoint || ""} onChange={(e) => setField("wa_wablas_endpoint", e.target.value)} placeholder="https://wablas.com/api/send-message" className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white font-mono" data-testid="wa-wablas-endpoint" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-slate-300 mb-1">Token</label>
                        <input type={showToken ? "text" : "password"} value={form.wa_wablas_token || ""} onChange={(e) => setField("wa_wablas_token", e.target.value)} placeholder="Token Wablas" className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white font-mono" data-testid="wa-wablas-token" />
                      </div>
                      <div>
                        <label className="block text-xs text-slate-300 mb-1">Secret Key (opsional)</label>
                        <input type={showToken ? "text" : "password"} value={form.wa_wablas_secret || ""} onChange={(e) => setField("wa_wablas_secret", e.target.value)} placeholder="Secret key jika diminta akun" className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white font-mono" data-testid="wa-wablas-secret" />
                      </div>
                    </div>
                  </div>
                )}
                {form.wa_provider === "watzap" && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="watzap-fields">
                    <div>
                      <label className="block text-xs text-slate-300 mb-1">API Key</label>
                      <input type={showToken ? "text" : "password"} value={form.wa_watzap_api_key || ""} onChange={(e) => setField("wa_watzap_api_key", e.target.value)} placeholder="API Key Watzap" className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white font-mono" data-testid="wa-watzap-apikey" />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-300 mb-1">Number Key</label>
                      <input type="text" value={form.wa_watzap_number_key || ""} onChange={(e) => setField("wa_watzap_number_key", e.target.value)} placeholder="Number Key perangkat" className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white font-mono" data-testid="wa-watzap-numberkey" />
                    </div>
                  </div>
                )}
                {form.wa_provider === "twilio" && (
                  <div className="space-y-3" data-testid="twilio-wa-fields">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-slate-300 mb-1">Account SID</label>
                        <input type={showToken ? "text" : "password"} value={form.wa_twilio_sid || ""} onChange={(e) => setField("wa_twilio_sid", e.target.value)} placeholder="ACxxxxxxxx" className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white font-mono" data-testid="wa-twilio-sid" />
                      </div>
                      <div>
                        <label className="block text-xs text-slate-300 mb-1">Auth Token</label>
                        <input type={showToken ? "text" : "password"} value={form.wa_twilio_token || ""} onChange={(e) => setField("wa_twilio_token", e.target.value)} placeholder="Auth Token" className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white font-mono" data-testid="wa-twilio-token" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs text-slate-300 mb-1">WhatsApp From Number (mis. +14155238886)</label>
                      <input type="text" value={form.wa_twilio_from || ""} onChange={(e) => setField("wa_twilio_from", e.target.value)} placeholder="+14155238886" className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white font-mono" data-testid="wa-twilio-from" />
                    </div>
                  </div>
                )}
                {form.wa_provider !== "wa_me" && (
                  <button type="button" onClick={() => setShowToken((s) => !s)} className="text-[11px] text-slate-400 hover:text-slate-200 flex items-center gap-1">
                    {showToken ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />} {showToken ? "Sembunyikan" : "Tampilkan"} kredensial
                  </button>
                )}
              </div>

              <div className="p-4 bg-slate-900 border border-slate-800 rounded-xl space-y-3">
                <h3 className="text-sm font-bold text-white flex items-center gap-2"><MessageSquare className="w-4 h-4 text-emerald-400" />Template Pesan</h3>
                <div className="p-2.5 bg-slate-950 border border-slate-800 rounded-lg space-y-1.5">
                  <p className="text-[10px] text-slate-400 font-semibold">Variabel bawaan (klik nama untuk melihat, gunakan format <code className="text-emerald-400">{"{variabel}"}</code>):</p>
                  <div className="flex flex-wrap gap-1">
                    {["nama","no_hp","no_layanan","alamat","paket","no_invoice","periode","total","nominal","diskon","sisa","jatuh_tempo","tanggal_terbit","tanggal_bayar","status","metode","link_bayar","link_portal","rt","rw","perusahaan","badan_usaha","cs","telepon","website"].map((v) => (
                      <code key={v} className="px-1.5 py-0.5 bg-slate-800 text-emerald-400 rounded text-[10px] font-mono">{`{${v}}`}</code>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-xs text-slate-300 mb-1">Reminder Tagihan Belum Bayar</label>
                  <textarea rows={4} value={form.wa_template_tagihan} onChange={(e) => setField("wa_template_tagihan", e.target.value)} className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white" data-testid="wa-template-tagihan-input" />
                </div>
                <div>
                  <label className="block text-xs text-slate-300 mb-1">Konfirmasi Pembayaran Lunas</label>
                  <textarea rows={3} value={form.wa_template_lunas} onChange={(e) => setField("wa_template_lunas", e.target.value)} className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white" data-testid="wa-template-lunas-input" />
                </div>
                <div>
                  <label className="block text-xs text-slate-300 mb-1">Notifikasi Isolir</label>
                  <textarea rows={3} value={form.wa_template_isolir} onChange={(e) => setField("wa_template_isolir", e.target.value)} className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white" data-testid="wa-template-isolir-input" />
                </div>

                <div className="pt-3 border-t border-slate-800">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h4 className="text-xs font-bold text-white">Variabel Kustom</h4>
                      <p className="text-[10px] text-slate-500">Buat variabel sendiri, mis. key <code className="text-emerald-400">rekening</code> → nilai <code className="text-emerald-400">BCA 12345 a/n PT ...</code>. Pakai di template dengan <code className="text-emerald-400">{"{rekening}"}</code>.</p>
                    </div>
                    <button type="button" onClick={addCustomVar} data-testid="add-custom-var-btn" className="px-2.5 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white text-[11px] font-bold rounded-lg whitespace-nowrap">+ Variabel</button>
                  </div>
                  <div className="space-y-2">
                    {(form.wa_custom_vars || []).length === 0 && (
                      <p className="text-[10px] text-slate-600 italic">Belum ada variabel kustom.</p>
                    )}
                    {(form.wa_custom_vars || []).map((cv, i) => (
                      <div key={i} className="flex items-center gap-2" data-testid={`custom-var-row-${i}`}>
                        <input type="text" value={cv.key} onChange={(e) => updateCustomVar(i, "key", e.target.value.replace(/[^a-zA-Z0-9_]/g, ""))} placeholder="nama_variabel" className="w-1/3 px-2 py-1.5 bg-slate-950 border border-slate-700 rounded-lg text-xs text-emerald-400 font-mono" data-testid={`custom-var-key-${i}`} />
                        <input type="text" value={cv.value} onChange={(e) => updateCustomVar(i, "value", e.target.value)} placeholder="nilai yang akan ditampilkan" className="flex-1 px-2 py-1.5 bg-slate-950 border border-slate-700 rounded-lg text-xs text-white" data-testid={`custom-var-value-${i}`} />
                        <button type="button" onClick={() => removeCustomVar(i)} className="p-1.5 text-rose-400 hover:text-rose-300" data-testid={`remove-custom-var-${i}`}><X className="w-4 h-4" /></button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* TAB PAYMENT */}
          {activeTab === "payment" && (
            <div className="space-y-5" data-testid="tab-payment-content">
              <div className="p-4 bg-slate-900 border border-slate-800 rounded-xl space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-bold text-white flex items-center gap-2"><CreditCard className="w-4 h-4 text-amber-400" />Payment Gateway</h3>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" checked={form.payment_enabled} onChange={(e) => setField("payment_enabled", e.target.checked)} className="sr-only peer" data-testid="payment-enabled-toggle" />
                    <div className="w-10 h-5 bg-slate-700 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-amber-600"></div>
                  </label>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-slate-300 mb-1">Provider</label>
                    <select value={form.payment_provider} onChange={(e) => setField("payment_provider", e.target.value)} className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white" data-testid="payment-provider-select">
                      <option value="tripay">Tripay (Indonesia — QRIS/VA)</option>
                      <option value="midtrans">Midtrans (Indonesia — Snap QRIS/VA/E-Wallet)</option>
                      <option value="xendit">Xendit (Indonesia — Hosted Invoice)</option>
                      <option value="stripe">Stripe (Global — integrasi terpisah)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-slate-300 mb-1">Mode</label>
                    <select value={form.payment_mode} onChange={(e) => setField("payment_mode", e.target.value)} className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white" data-testid="payment-mode-select">
                      <option value="sandbox">Sandbox / Test</option>
                      <option value="production">Production / Live</option>
                    </select>
                  </div>
                </div>

                {/* Provider-specific credentials */}
                {form.payment_provider === "tripay" && (
                  <div className="space-y-3" data-testid="tripay-fields">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-slate-300 mb-1">Tripay API Key</label>
                        <input type={showServerKey ? "text" : "password"} value={form.tripay_api_key || ""} onChange={(e) => setField("tripay_api_key", e.target.value)} placeholder="DEV-xxxxxxxx" className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white font-mono" data-testid="tripay-api-key" />
                      </div>
                      <div>
                        <label className="block text-xs text-slate-300 mb-1">Tripay Private Key</label>
                        <input type={showServerKey ? "text" : "password"} value={form.tripay_private_key || ""} onChange={(e) => setField("tripay_private_key", e.target.value)} placeholder="xxxxx-xxxxx-xxxxx" className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white font-mono" data-testid="tripay-private-key" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs text-slate-300 mb-1">Merchant Code</label>
                      <input type="text" value={form.tripay_merchant_code || ""} onChange={(e) => setField("tripay_merchant_code", e.target.value)} placeholder="TXXXXX" className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white font-mono" data-testid="tripay-merchant-code" />
                    </div>
                    <div className="p-3 bg-slate-950 border border-amber-500/30 rounded-xl">
                      <p className="text-[10px] text-amber-400 font-semibold mb-1">Callback URL — daftarkan di dashboard Tripay:</p>
                      <code className="text-[11px] text-cyan-300 break-all font-mono">{BACKEND_URL}/api/payments/tripay/callback</code>
                    </div>
                  </div>
                )}
                {form.payment_provider === "xendit" && (
                  <div className="space-y-3" data-testid="xendit-fields">
                    <div>
                      <label className="block text-xs text-slate-300 mb-1">Xendit Secret API Key</label>
                      <input type={showServerKey ? "text" : "password"} value={form.xendit_secret_key || ""} onChange={(e) => setField("xendit_secret_key", e.target.value)} placeholder="xnd_development_xxxx" className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white font-mono" data-testid="xendit-secret-key" />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-300 mb-1">Webhook Verification Token</label>
                      <input type={showServerKey ? "text" : "password"} value={form.xendit_webhook_token || ""} onChange={(e) => setField("xendit_webhook_token", e.target.value)} placeholder="Token dari Settings → Webhook" className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white font-mono" data-testid="xendit-webhook-token" />
                    </div>
                    <div className="p-3 bg-slate-950 border border-amber-500/30 rounded-xl">
                      <p className="text-[10px] text-amber-400 font-semibold mb-1">Webhook URL — daftarkan di Xendit → Settings → Webhook (Invoices paid):</p>
                      <code className="text-[11px] text-cyan-300 break-all font-mono">{BACKEND_URL}/api/payments/xendit/webhook</code>
                    </div>
                  </div>
                )}
                {form.payment_provider === "midtrans" && (
                  <div className="space-y-3" data-testid="midtrans-fields">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-slate-300 mb-1">Server Key</label>
                        <input type={showServerKey ? "text" : "password"} value={form.midtrans_server_key || ""} onChange={(e) => setField("midtrans_server_key", e.target.value)} placeholder="SB-Mid-server-xxxx" className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white font-mono" data-testid="midtrans-server-key" />
                      </div>
                      <div>
                        <label className="block text-xs text-slate-300 mb-1">Client Key</label>
                        <input type="text" value={form.midtrans_client_key || ""} onChange={(e) => setField("midtrans_client_key", e.target.value)} placeholder="SB-Mid-client-xxxx" className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white font-mono" data-testid="midtrans-client-key" />
                      </div>
                    </div>
                    <div className="p-3 bg-slate-950 border border-amber-500/30 rounded-xl">
                      <p className="text-[10px] text-amber-400 font-semibold mb-1">Payment Notification URL — daftarkan di Midtrans → Settings → Configuration:</p>
                      <code className="text-[11px] text-cyan-300 break-all font-mono">{BACKEND_URL}/api/payments/midtrans/notification</code>
                    </div>
                  </div>
                )}
                {form.payment_provider === "stripe" && (
                  <div className="p-3 bg-slate-950 border border-slate-700 rounded-xl text-xs text-slate-400" data-testid="stripe-note">
                    Integrasi Stripe dilakukan terpisah. Beri tahu saya jika ingin mengaktifkan Stripe.
                  </div>
                )}
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <button type="button" onClick={() => setShowServerKey((s) => !s)} className="text-[11px] text-slate-400 hover:text-slate-200 flex items-center gap-1">
                    {showServerKey ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />} {showServerKey ? "Sembunyikan" : "Tampilkan"} kredensial
                  </button>
                  {form.payment_provider !== "stripe" && (
                    <button type="button" onClick={handleTestPayment} disabled={payTest.loading} className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-amber-500/15 text-amber-300 border border-amber-500/40 hover:bg-amber-500/25 disabled:opacity-50 flex items-center gap-1.5" data-testid="payment-test-connection-btn">
                      <Zap className="w-3.5 h-3.5" /> {payTest.loading ? "Menguji..." : "Tes Koneksi Gateway"}
                    </button>
                  )}
                </div>
                {payTest.message && (
                  <div className={`p-2.5 rounded-lg text-xs border ${payTest.ok ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-300" : "bg-rose-500/10 border-rose-500/40 text-rose-300"}`} data-testid="payment-test-result">
                    {payTest.message}
                  </div>
                )}
              </div>

              <div className="p-4 bg-slate-900 border border-slate-800 rounded-xl space-y-3">
                <h3 className="text-sm font-bold text-white flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-amber-400" />Metode Pembayaran Aktif</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <label className={`flex items-center gap-2 p-3 rounded-xl border cursor-pointer transition-colors ${form.payment_qris_enabled ? "border-amber-500/50 bg-amber-500/10" : "border-slate-700 bg-slate-950"}`}>
                    <input type="checkbox" checked={form.payment_qris_enabled} onChange={(e) => setField("payment_qris_enabled", e.target.checked)} className="w-4 h-4 accent-amber-500" data-testid="qris-toggle" />
                    <div>
                      <p className="text-xs font-bold text-white">QRIS</p>
                      <p className="text-[10px] text-slate-500">Semua e-wallet & bank</p>
                    </div>
                  </label>
                  <label className={`flex items-center gap-2 p-3 rounded-xl border cursor-pointer transition-colors ${form.payment_va_enabled ? "border-amber-500/50 bg-amber-500/10" : "border-slate-700 bg-slate-950"}`}>
                    <input type="checkbox" checked={form.payment_va_enabled} onChange={(e) => setField("payment_va_enabled", e.target.checked)} className="w-4 h-4 accent-amber-500" data-testid="va-toggle" />
                    <div>
                      <p className="text-xs font-bold text-white">Virtual Account</p>
                      <p className="text-[10px] text-slate-500">BCA, Mandiri, BNI, dsb.</p>
                    </div>
                  </label>
                  <label className={`flex items-center gap-2 p-3 rounded-xl border cursor-pointer transition-colors ${form.payment_ewallet_enabled ? "border-amber-500/50 bg-amber-500/10" : "border-slate-700 bg-slate-950"}`}>
                    <input type="checkbox" checked={form.payment_ewallet_enabled} onChange={(e) => setField("payment_ewallet_enabled", e.target.checked)} className="w-4 h-4 accent-amber-500" data-testid="ewallet-toggle" />
                    <div>
                      <p className="text-xs font-bold text-white">E-Wallet</p>
                      <p className="text-[10px] text-slate-500">OVO, DANA, ShopeePay</p>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* TAB VISIBILITY */}
          {activeTab === "visibility" && (
            <div className="space-y-5" data-testid="tab-visibility-content">
              {/* MODUL AKTIF */}
              <div className="p-4 bg-slate-900 border border-slate-800 rounded-xl space-y-3">
                <h3 className="text-sm font-bold text-white flex items-center gap-2"><Eye className="w-4 h-4 text-purple-400" />Modul Aktif</h3>
                <p className="text-[10px] text-slate-500">Aktifkan / sembunyikan setiap menu, termasuk submenu di dalam dropdown. Menu yang dinonaktifkan tidak muncul di sidebar.</p>
                <div className="space-y-3">
                  {MENU.map((m) => (
                    <div key={m.key} className="rounded-xl border border-slate-800 bg-slate-950 p-3">
                      <label className="flex items-center justify-between gap-3 cursor-pointer">
                        <div className="flex items-center gap-2">
                          <m.icon className="w-4 h-4 text-purple-400" />
                          <span className="text-xs font-bold text-white">{m.label}</span>
                        </div>
                        <div className="relative inline-flex items-center">
                          <input type="checkbox" checked={moduleVisible(m.key)} onChange={() => toggleModuleVisible(m.key)} className="sr-only peer" data-testid={`visibility-${m.key}`} />
                          <div className="w-9 h-5 bg-slate-700 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-purple-600"></div>
                        </div>
                      </label>
                      {m.children && moduleVisible(m.key) && (
                        <div className="mt-2 pl-6 border-l-2 border-slate-800 space-y-1.5">
                          {m.children.map((ch) => (
                            <label key={ch.key} className="flex items-center justify-between gap-3 cursor-pointer py-1">
                              <div className="flex items-center gap-2">
                                <ch.icon className="w-3.5 h-3.5 text-slate-400" />
                                <span className="text-[11px] text-slate-300">{ch.label}</span>
                              </div>
                              <div className="relative inline-flex items-center">
                                <input type="checkbox" checked={moduleVisible(ch.key)} onChange={() => toggleModuleVisible(ch.key)} className="sr-only peer" data-testid={`visibility-${ch.key}`} />
                                <div className="w-8 h-4 bg-slate-700 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-purple-600"></div>
                              </div>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* AKSES PER ROLE / HAK AKSES */}
              <div className="p-4 bg-slate-900 border border-slate-800 rounded-xl space-y-3">
                <h3 className="text-sm font-bold text-white flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-purple-400" />Akses per Role / Hak Akses</h3>
                <p className="text-[10px] text-slate-500">Centang menu yang boleh diakses tiap role (termasuk submenu). Admin selalu akses penuh. Anda bisa menambah hak akses baru, mis. Investor.</p>

                {/* Tambah role baru */}
                <div className="flex flex-col sm:flex-row gap-2 p-3 rounded-lg bg-slate-950 border border-slate-800">
                  <input type="text" value={newRole.label} onChange={(e) => setNewRole({ ...newRole, label: e.target.value })} placeholder="Nama hak akses (mis. Investor)" data-testid="new-role-label" className="flex-1 px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-100" />
                  <input type="text" value={newRole.desc} onChange={(e) => setNewRole({ ...newRole, desc: e.target.value })} placeholder="Deskripsi (opsional)" className="flex-1 px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-100" />
                  <button type="button" onClick={addCustomRole} data-testid="add-role-btn" className="px-3 py-2 bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold rounded-xl flex items-center justify-center gap-1.5"><Plus className="w-4 h-4" />Tambah Role</button>
                </div>

                {allRoles.map((r) => (
                  <div key={r.id} className="p-3 rounded-lg bg-slate-950 border border-slate-800" data-testid={`role-${r.id}-config`}>
                    <div className="mb-2 flex items-center justify-between">
                      <div>
                        <p className="text-xs font-bold text-white">{r.label} <span className="text-[10px] text-slate-500 font-mono">({r.id})</span></p>
                        <p className="text-[10px] text-slate-500">{r.desc}</p>
                      </div>
                      {r.custom && <button type="button" onClick={() => removeCustomRole(r.id)} data-testid={`remove-role-${r.id}`} className="p-1.5 text-rose-400 hover:bg-rose-950/60 rounded-lg"><Trash2 className="w-3.5 h-3.5" /></button>}
                    </div>
                    {r.id === "admin" ? (
                      <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">✓ Akses Penuh (semua menu)</span>
                    ) : (
                      <div className="space-y-2">
                        {MENU.map((m) => (
                          <div key={m.key}>
                            <button type="button" onClick={() => toggleRoleModule(r.id, m.key)} data-testid={`role-${r.id}-${m.key}`} className={chipCls(roleHas(r.id, m.key))}>{roleHas(r.id, m.key) ? "✓ " : ""}{m.label}</button>
                            {m.children && (
                              <div className="flex flex-wrap gap-1.5 mt-1 pl-4">
                                {m.children.map((ch) => (
                                  <button key={ch.key} type="button" onClick={() => toggleRoleModule(r.id, ch.key)} data-testid={`role-${r.id}-${ch.key}`} className={chipCls(roleHas(r.id, ch.key), true)}>{roleHas(r.id, ch.key) ? "✓ " : ""}{ch.label}</button>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions - sticky footer */}
          <div className="pt-3 flex items-center justify-between border-t border-slate-800">
            <p className="text-[10px] text-slate-500 flex items-center gap-1"><Info className="w-3 h-3" />Perubahan akan aktif segera setelah disimpan</p>
            <button type="submit" disabled={saving} data-testid="save-aplikasi-btn" className="px-5 py-2.5 bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-500 hover:to-cyan-500 text-white font-bold text-xs rounded-xl shadow-lg shadow-teal-600/30 flex items-center gap-2 disabled:opacity-50">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              <span>{saving ? "Menyimpan..." : "Simpan Pengaturan"}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
